import type { SupabaseClient } from '@supabase/supabase-js';

export type Group = {
    id: string;
    name: string;
    created_by: string;
    created_at: string;
    slug?: string | null;
    plan?: 'personal' | 'family' | 'business';
    description?: string | null;
    start_date?: string | null;
    image_url?: string | null;
};

export type GroupMemberRole = 'admin' | 'member' | 'owner';

export type GroupInvite = {
    id: string;
    group_id: string;
    email: string;
    invited_by: string;
    token: string;
    created_at: string;
    desired_role?: string | null;
};

export async function listUserGroupsCore(supabase: SupabaseClient) {
    const { data: mems, error: memErr } = await supabase
        .from('vault_members')
        .select('group_id, role');
    if (memErr) throw memErr;
    const memberships = (mems || []) as Array<{ group_id: string; role: GroupMemberRole }>;
    if (memberships.length === 0) return [] as Array<{ group: Group; role: GroupMemberRole }>;

    const ids = Array.from(new Set(memberships.map(m => m.group_id)));
    const { data: vaults, error: vErr } = await supabase
        .from('vaults')
        .select('id, name, created_by, created_at, slug, plan, image_url, deleted_at')
        .in('id', ids);
    if (vErr) throw vErr;
    // Exclude soft-deleted vaults
    const activeVaults = (vaults || []).filter((v: any) => !v.deleted_at);

    const roleById = new Map<string, GroupMemberRole>();
    memberships.forEach(m => { if (!roleById.has(m.group_id)) roleById.set(m.group_id, m.role); });

    const items = (activeVaults || []).map(v => ({
        group: v as Group,
        role: (roleById.get((v as any).id) || 'member') as GroupMemberRole,
    }));
    items.sort((a, b) => {
        const av = a.group?.created_at ? Date.parse(a.group.created_at) : 0;
        const bv = b.group?.created_at ? Date.parse(b.group.created_at) : 0;
        return bv - av;
    });
    return items;
}

export async function createGroupCore(supabase: SupabaseClient, name: string) {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('Must be signed in');
    const payload = { name, created_by: user.id };
    const { data, error } = await supabase.from('vaults').insert(payload).select('*').single();
    if (error) throw error;
    return data as Group;
}

/**
 * Create a new vault with a required slug and optional display name.
 * - Normalizes and validates the slug (lowercase, a-z/0-9/- only, 3+ chars)
 * - Falls back to a prettified name from slug if name is not provided
 * - Sets created_by to the current authenticated user
 *
 * Throws on auth errors, invalid input, or DB errors (including unique slug violation).
 */
export async function createVaultCore(supabase: SupabaseClient, slug: string, name?: string) {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('Must be signed in');

    const normalized = String(slug || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');

    // Basic validation and reserved words guard (mirrors UI constraints)
    const reserved = new Set(['auth', 'login', 'reset-password', 'code', 'preview']);
    if (!normalized || normalized.length < 3 || reserved.has(normalized)) {
        throw new Error('Invalid or reserved slug');
    }

    const displayName = (name && name.trim())
        ? name.trim()
        : normalized
            .split('-')
            .filter(Boolean)
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');

    const payload = { name: displayName, slug: normalized, created_by: user.id } as Partial<Group> & { created_by: string };
    const { data, error } = await supabase
        .from('vaults')
        .insert(payload as any)
        .select('*')
        .single();
    if (error) {
        // Map common unique-violation codes to a friendlier message
        const code = (error as any)?.code;
        if (code === '23505') {
            throw new Error('This vault link is already taken');
        }
        throw error;
    }
    return data as Group;
}

export async function findGroupBySlugCore(supabase: SupabaseClient, slug: string) {
    const { data, error } = await supabase
        .from('vaults')
        .select('id, name, created_by, created_at, slug, plan, image_url, deleted_at')
        .ilike('slug', slug)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    if ((data as any).deleted_at) return null;
    return data as Group | null;
}

/**
 * Exact slug match (case sensitive-normalized). Use for availability checks.
 */
export async function getGroupBySlugExactCore(supabase: SupabaseClient, slug: string) {
    const clean = (slug || '').trim();
    if (!clean) return null;
    const { data, error } = await supabase
        .from('vaults')
        .select('id, name, created_by, created_at, slug, plan, image_url, deleted_at')
        .eq('slug', clean)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    if ((data as any).deleted_at) return null;
    return (data as any) || null;
}

/**
 * Search vaults by slug or name using ilike. Returns up to 10 results sorted by created_at desc.
 */
export async function listVaultsBySlugSearchCore(supabase: SupabaseClient, term: string) {
    const clean = (term || '').trim();
    if (!clean) return [];
    const { data, error } = await supabase
        .from('vaults')
        .select('id, name, created_by, created_at, slug, plan, image_url, deleted_at')
        .or(`name.ilike.%${clean}%,slug.ilike.%${clean}%`)
        .order('created_at', { ascending: false })
        .limit(10);
    if (error) throw error;
    return ((data || []) as any[]).filter(v => !v.deleted_at) as Group[];
}

export async function updateGroupCore(
    supabase: SupabaseClient,
    id: string,
    patch: Partial<Pick<Group, 'name' | 'description' | 'start_date' | 'image_url' | 'plan' | 'slug'>>
) {
    const { data, error } = await supabase
        .from('vaults')
        .update(patch)
        .eq('id', id)
        .select('*')
        .maybeSingle();
    if (error) throw error;
    return data as Group | null;
}

export async function inviteToGroupCore(
    supabase: SupabaseClient,
    groupId: string,
    email: string,
    desiredRole?: 'admin' | 'member'
) {
    const user = (await supabase.auth.getUser()).data.user;
    if (!user) throw new Error('Must be signed in');
    const normalizedEmail = (email || '').trim().toLowerCase();
    let lastErr: any = null;
    for (let attempt = 0; attempt < 3; attempt++) {
        const code = String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0');
        const { data, error } = await supabase
            .from('vault_invites')
            .insert({ group_id: groupId, email: normalizedEmail, invited_by: user.id, desired_role: desiredRole, token: code } as any)
            .select('*')
            .single();
        if (!error) {
            return ({ ...(data as any), token: (data as any).token ?? code }) as GroupInvite;
        }
        lastErr = error;
        if ((error as any)?.code !== '23505') break;
    }
    throw lastErr || new Error('Failed to create invite');
}

export async function acceptInviteCore(supabase: SupabaseClient, token: string) {
    const { data, error } = await supabase.rpc('accept_group_invite', { p_token: token });
    if (error) throw error;
    return data as boolean;
}

export async function getInviteByTokenCore(supabase: SupabaseClient, token: string) {
    const { data: invite, error } = await supabase
        .from('vault_invites')
        .select('id, group_id, email, invited_by, token, created_at')
        .eq('token', token)
        .maybeSingle();
    if (error) throw error;
    if (!invite) return null;
    let vault: Group | null = null;
    try {
        const { data: v } = await supabase
            .from('vaults')
            .select('id, name, slug, image_url, created_by, created_at')
            .eq('id', (invite as any).group_id)
            .maybeSingle();
        vault = (v as any) || null;
    } catch {
        vault = null;
    }
    return { invite: invite as any as GroupInvite, vault } as { invite: GroupInvite; vault: Group | null };
}

export async function getInviteOtpForDevCore(supabase: SupabaseClient, token: string) {
    const { data, error } = await supabase.rpc('get_invite_otp_for_dev', { p_token: token });
    if (error) throw error;
    return data as string | null;
}

export async function verifyInviteCodeCore(supabase: SupabaseClient, token: string, code: string) {
    const clean = (code || '').replace(/[^0-9]/g, '').slice(0, 6);
    if (clean.length !== 6) throw new Error('Invalid code format');
    if (token !== clean) return false;
    const { error: accErr } = await supabase.rpc('accept_group_invite', { p_token: token });
    if (accErr) throw accErr;
    return true;
}

export async function listGroupInvitesCore(supabase: SupabaseClient, groupId: string) {
    const { data, error } = await supabase
        .from('vault_invites')
        .select('id, group_id, email, invited_by, desired_role, token, created_at')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Array<GroupInvite>;
}

export async function listGroupContactsCore(supabase: SupabaseClient, groupId: string) {
    const { data, error } = await supabase
        .from('contact_groups')
        .select('contact_id, contacts:contact_id ( * )')
        .eq('group_id', groupId);
    if (error) throw error;
    return (data || []).map((row: any) => row.contacts);
}


export type GroupMember = { user_id: string; role: GroupMemberRole };

export async function listGroupMembersCore(supabase: SupabaseClient, groupId: string) {
    if (!groupId) throw new Error('groupId required');
    const { data, error } = await supabase
        .from('vault_members')
        .select('user_id, role')
        .eq('group_id', groupId);
    if (error) throw error;
    return (data || []) as GroupMember[];
}

/**
 * Bulk fetch vaults by ids with minimal fields for UI enrichment.
 */
export async function listGroupsByIdsCore(
    supabase: SupabaseClient,
    ids: string[]
): Promise<Array<Pick<Group, 'id' | 'name' | 'slug' | 'image_url'>>> {
    const uniq = Array.from(new Set((ids || []).filter(Boolean)));
    if (uniq.length === 0) return [];
    const { data, error } = await supabase
        .from('vaults')
        .select('id, name, slug, image_url')
        .in('id', uniq as any);
    if (error) throw error;
    return (data || []) as any;
}

/**
 * Returns group by id with common fields or null.
 */
export async function getGroupByIdCore(supabase: SupabaseClient, id: string): Promise<Group | null> {
    if (!id) throw new Error('id required');
    const { data, error } = await supabase
        .from('vaults')
        .select('id, name, created_by, created_at, slug, plan, image_url, deleted_at')
        .eq('id', id)
        .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    if ((data as any).deleted_at) return null;
    return (data as any) || null;
}

/**
 * Enriched member list for Settings UI: includes name/email/avatar via user_profiles and contacts.
 * Owner is normalized via the highest role or via matching of group creator.
 */
export async function listVaultMembersWithProfilesCore(
    supabase: SupabaseClient,
    groupId: string
): Promise<Array<{ user_id: string; role: GroupMemberRole | 'owner'; email?: string; name?: string; avatar_url?: string }>> {
    if (!groupId) throw new Error('groupId required');
    const members = await listGroupMembersCore(supabase, groupId);
    if (members.length === 0) return [];
    const userIds = Array.from(new Set(members.map(m => m.user_id))).filter(Boolean);

    // Profiles
    const { data: profs, error: pErr } = await supabase
        .from('user_profiles')
        .select('user_id, first_name, last_name, email, name, display_name, avatar_url, photo_url, claimed_contact_id')
        .in('user_id', userIds as any);
    if (pErr) throw pErr;
    const profByUser: Record<string, any> = {};
    for (const p of (profs || []) as any[]) profByUser[p.user_id] = p;

    // Member-chosen contacts mapping per vault
    const { data: vmc, error: vmcErr } = await supabase
        .from('vault_member_contacts')
        .select('user_id, contact_id')
        .eq('group_id', groupId);
    if (vmcErr) {
        console.error('RLS ERROR BLOCKING VAULT MEMBER CONTACTS:', vmcErr);
        console.error('This is why you cannot see representative contacts!');
        // Not fatal; proceed without chosen contacts (but log the error)
    }
    const chosenByUser: Record<string, string> = {};
    for (const row of (vmc || []) as Array<{ user_id: string; contact_id: string }>) {
        chosenByUser[row.user_id] = row.contact_id;
    }

    // Contacts to load include claimed_contact_id and chosen contact ids
    const contactIds = Array.from(new Set([
        ...(profs || []).map((p: any) => p.claimed_contact_id).filter(Boolean),
        ...Object.values(chosenByUser)
    ]));
    let contactById: Record<string, { name?: string; avatar_url?: string }> = {};
    if (contactIds.length) {
        const { data: contacts, error: cErr } = await supabase
            .from('contacts')
            .select('id, first_name, last_name, profile_image_url, photo')
            .in('id', contactIds as any);
        if (cErr) throw cErr;
        for (const c of (contacts || []) as any[]) {
            const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || undefined;
            const avatar_url = c.profile_image_url || c.photo || undefined;
            contactById[c.id] = { name, avatar_url };
        }
    }

    // Owner normalization: check if any explicit owner; otherwise use group created_by
    let ownerUserId: string | null = null;
    const explicitOwner = members.find(m => m.role === 'owner');
    if (explicitOwner) ownerUserId = explicitOwner.user_id;
    if (!ownerUserId) {
        try {
            const g = await getGroupByIdCore(supabase, groupId);
            ownerUserId = g?.created_by || null;
        } catch { /* ignore */ }
    }

    return members.map(m => {
        const prof = profByUser[m.user_id] || {};
        const chosenId = chosenByUser[m.user_id];
        const cChosen = chosenId ? contactById[chosenId] : undefined;
        const cClaimed = prof.claimed_contact_id ? contactById[prof.claimed_contact_id] : undefined;
        const contactPref = cChosen || cClaimed;
        const displayName = contactPref?.name || prof.name || prof.display_name || [prof.first_name, prof.last_name].filter(Boolean).join(' ');
        const avatar_url = contactPref?.avatar_url || prof.avatar_url || prof.photo_url;
        const normalizedRole: any = ownerUserId && m.user_id === ownerUserId ? 'owner' : (m.role || 'member');

        // Debug logging to see what's happening
        console.log('Processing member:', {
            user_id: m.user_id,
            prof,
            chosenId,
            cChosen,
            cClaimed,
            contactPref,
            displayName,
            avatar_url,
            normalizedRole
        });

        return { user_id: m.user_id, role: normalizedRole, email: prof.email || undefined, name: displayName || undefined, avatar_url };
    });
}
// Set the chosen contact for the current user in a vault. Enforces uniqueness at DB level.
export async function setMemberChosenContactCore(
    supabase: SupabaseClient,
    groupId: string,
    userId: string,
    contactId: string
) {
    if (!groupId || !userId || !contactId) throw new Error('groupId, userId, contactId required');
    const { error } = await supabase
        .from('vault_member_contacts')
        .upsert({ group_id: groupId, user_id: userId, contact_id: contactId }, { onConflict: 'group_id,user_id' });
    if (error) {
        // Postgres unique violation => contact already chosen by someone else in this vault
        const msg = (error as any)?.message || '';
        if (/duplicate key value violates unique constraint/i.test(msg) || /uq_vmc_group_contact/i.test(msg)) {
            throw new Error('Sorry, this contact is already chosen by another member in this vault.');
        }
        throw error;
    }
}

export async function clearMemberChosenContactCore(
    supabase: SupabaseClient,
    groupId: string,
    userId: string
) {
    if (!groupId || !userId) throw new Error('groupId and userId required');
    const { error } = await supabase
        .from('vault_member_contacts')
        .delete()
        .match({ group_id: groupId, user_id: userId });
    if (error) throw error;
}
/**
 * Expand group members into their claimed contact names for UI selection.
 * Returns sorted list by display name.
 */
export async function listGroupMemberContactsCore(
    supabase: SupabaseClient,
    groupId: string
): Promise<Array<{ contact_id: string; name: string }>> {
    if (!groupId) throw new Error('groupId required');
    const members = await listGroupMembersCore(supabase, groupId);
    const userIds = Array.from(new Set(members.map(m => m.user_id))).filter(Boolean);
    if (userIds.length === 0) return [];

    const { data: profs, error: pErr } = await supabase
        .from('user_profiles')
        .select('user_id, claimed_contact_id')
        .in('user_id', userIds as any);
    if (pErr) throw pErr;
    const contactIds = Array.from(new Set((profs || []).map((p: any) => p.claimed_contact_id).filter(Boolean)));
    if (contactIds.length === 0) return [];

    const { data: contacts, error: cErr } = await supabase
        .from('contacts')
        .select('id, first_name, last_name')
        .in('id', contactIds as any);
    if (cErr) throw cErr;
    const opts = (contacts || []).map((c: any) => ({
        contact_id: c.id,
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unnamed',
    }));
    opts.sort((a, b) => a.name.localeCompare(b.name));
    return opts;
}

export async function updateGroupMemberRoleCore(
    supabase: SupabaseClient,
    groupId: string,
    userId: string,
    role: GroupMemberRole
) {
    if (!groupId || !userId) throw new Error('groupId and userId required');
    const { error } = await supabase
        .from('vault_members')
        .update({ role })
        .match({ group_id: groupId, user_id: userId });
    if (error) throw error;
}

export async function removeGroupMemberCore(supabase: SupabaseClient, groupId: string, userId: string) {
    if (!groupId || !userId) throw new Error('groupId and userId required');
    const { error } = await supabase
        .from('vault_members')
        .delete()
        .match({ group_id: groupId, user_id: userId });
    if (error) throw error;
}

export async function revokeInviteCore(supabase: SupabaseClient, inviteId: string) {
    if (!inviteId) throw new Error('inviteId required');
    const { error } = await supabase.from('vault_invites').delete().eq('id', inviteId);
    if (error) throw error;
}

// New: delete invite by token (used by Settings/HomePage UIs)
export async function deleteInviteByTokenCore(supabase: SupabaseClient, token: string) {
    if (!token) throw new Error('token required');
    const { error } = await supabase.from('vault_invites').delete().eq('token', token);
    if (error) throw error;
}

// New: transfer ownership via RPC
export async function transferOwnershipCore(supabase: SupabaseClient, groupId: string, newOwnerUserId: string) {
    if (!groupId || !newOwnerUserId) throw new Error('groupId and newOwnerUserId required');
    const { error } = await supabase.rpc('transfer_group_ownership', { p_group_id: groupId, p_new_owner: newOwnerUserId });
    if (error) throw error;
}

// New: list invites for a specific email (pending invites in HomePage)
export async function listInvitesForEmailCore(supabase: SupabaseClient, email: string) {
    const target = (email || '').trim().toLowerCase();
    if (!target) return [] as Array<GroupInvite>;
    const { data, error } = await supabase
        .from('vault_invites')
        .select('id, group_id, email, invited_by, token, created_at, desired_role')
        .eq('email', target)
        .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as any as Array<GroupInvite>;
}

// New: utility to list vaults for user with joined vault info and role
export async function listUserVaultsWithRoleCore(supabase: SupabaseClient, userId: string) {
    if (!userId) throw new Error('userId required');
    const { data: memberships, error } = await supabase
        .from('vault_members')
        .select('group_id, role, vaults:group_id ( id, name, slug, image_url, created_at, deleted_at )')
        .eq('user_id', userId);
    if (error) throw error;
    const rows = (memberships || []) as Array<any>;
    return rows
        .filter(r => !r.vaults?.deleted_at)
        .map(r => ({ ...r.vaults, role: r.role }));
}

/**
 * Ensure the creator of the vault has an owner membership row.
 * Throws if the vault doesn't exist or the user is not the creator, or on DB error.
 */
export async function ensureOwnerMembershipCore(
    supabase: SupabaseClient,
    groupId: string,
    userId: string
): Promise<void> {
    if (!groupId || !userId) throw new Error('groupId and userId required');
    const vault = await getGroupByIdCore(supabase, groupId);
    if (!vault) throw new Error('Vault not found');
    if (vault.created_by !== userId) throw new Error('User is not the vault creator');
    const { error } = await (supabase as any)
        .from('vault_members')
        .upsert({ group_id: groupId, user_id: userId, role: 'owner' }, { onConflict: 'group_id,user_id' });
    if (error) {
        // Enhanced error handling for the "personal vaults may only have an owner" constraint
        const errorMsg = error.message || '';
        const errorCode = error.code || '';

        if (errorMsg.includes('personal vaults may only have an owner')) {
            throw new Error(`Vault membership constraint error: This appears to be a database constraint preventing personal vault owner creation. The constraint has been deprecated in development. Error details: ${errorMsg}`);
        }

        console.error('Vault membership creation error:', {
            code: errorCode,
            message: errorMsg,
            details: error.details,
            hint: error.hint,
            groupId,
            userId
        });

        throw error;
    }
}

/**
 * Soft delete a vault by id. Sets deleted_at, deleted_by, delete_undo_until, and a unique delete_undo_token.
 * Returns the undo token and deadline. Does not remove related rows; UI should gate access after this call.
 */
export async function softDeleteVaultCore(
    supabase: SupabaseClient,
    groupId: string,
    userId: string,
    retentionDays: number = 10
): Promise<{ undoToken: string; undoUntil: string }> {
    if (!groupId || !userId) throw new Error('groupId and userId required');
    // Generate a random token client-side
    const token = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    const until = new Date(Date.now() + retentionDays * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
        .from('vaults')
        .update({ deleted_at: new Date().toISOString(), deleted_by: userId, delete_undo_until: until, delete_undo_token: token } as any)
        .eq('id', groupId);
    if (error) throw error;
    return { undoToken: token, undoUntil: until };
}

/**
 * Restore a soft-deleted vault using its undo token, only if within the undo window.
 */
export async function restoreVaultCore(
    supabase: SupabaseClient,
    undoToken: string
): Promise<boolean> {
    const clean = (undoToken || '').trim();
    if (!clean) throw new Error('undo token required');
    // Fetch by token, validate window, then clear fields
    const { data: row, error: selErr } = await supabase
        .from('vaults')
        .select('id, delete_undo_until')
        .eq('delete_undo_token', clean)
        .maybeSingle();
    if (selErr) throw selErr;
    if (!row) return false;
    if (row.delete_undo_until && new Date(row.delete_undo_until).getTime() < Date.now()) {
        return false;
    }
    const { error: updErr } = await supabase
        .from('vaults')
        .update({ deleted_at: null, deleted_by: null, delete_undo_until: null, delete_undo_token: null } as any)
        .eq('id', (row as any).id);
    if (updErr) throw updErr;
    return true;
}
